/*
 * Authors: Koen, mariushirjanu
 * Created: 07/08/2017
 * Upgraded: 08/11/2022
 */
import * as _ from 'underscore';
import { Input, Component, ElementRef, OnInit, OnDestroy, Inject, ViewChild, HostListener } from '@angular/core';
import { IExtendedWindow } from '../../../type_definitions/datatypes';
import { ScrollService } from '../scroll/scroll.service';
import { BrowserVendorService } from '../browser/browser-vendor.service';
import {  Subscription, timer } from 'rxjs';
import * as elementResizeDetector from 'element-resize-detector';

import { MAIN_CONTAINER_ELEMENT } from '../../main/main.component';

/**
 * @ngdoc component
 * @name ga2.common.components.fixedToTop:fixedToTop
 *
 * @description A component for fixing an element to the top of the page
 */

@Component({
  selector: 'fixed-to-top',
  template: `
    <div #content class="fixed-to-top-container">
      <ng-content></ng-content>
    </div>
  `
})
export class FixedToTopComponent implements OnInit, OnDestroy {
  @ViewChild('content', { static: true }) public content:ElementRef<HTMLDivElement>;
  @Input() public innerClass?:string;
  @Input() public isInDialog:boolean;

  private element:JQuery;
  private nativeElement:JQuery;
  private headerContainer:JQuery;
  private eventHandler:Subscription;
  private backupWidth:number;
  private backupPaddingTop:number;

  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private browserVendorService:BrowserVendorService,
    private scrollService:ScrollService
  ) {
    elementResizeDetector({ strategy: 'scroll' });
  }

  public ngOnInit():void {
    // when the wizard is open in the dialog -> do not apply fixation to top
    if(this.isInDialog){
      return;
    }

    this.nativeElement = $(this.content.nativeElement);
    // move to scrollContainer, this allows the element to be fixed relative to it
    // https://stackoverflow.com/questions/6794000/fixed-position-but-relative-to-container
    this.element = this.nativeElement.detach().prependTo(`#${MAIN_CONTAINER_ELEMENT}`);
    this.listen();
  }

  private listen():void {
    this.recalculateWidthAfterPageLoadListener();
  }

  // keep listening on window resize changes
  @HostListener('window:resize', ['$event.target'])
  private updateElementFixation = ():void => {
    if(this.isInDialog){
      //don't do anything when inside a modal dialog
      return;
    }

    const scrollContainer:JQuery<HTMLElement> = $(`#${MAIN_CONTAINER_ELEMENT}`);
    this.headerContainer = $('.container.main');
    const leftElements:JQuery<HTMLElement>[] = [
      $('.ui-layout-west'),
      $('.ui-layout-resizer-west')
    ];

    const calculateWidth:Function = ():number => {
      //we compute the width, because the clientWidth lags behind during resize events
      const documentWidth:number = $(document).width();
      const leftElementsWidth:number = _.reduce(leftElements, (memo:number, leftElm:JQuery<HTMLElement>) => memo + (leftElm.is(':visible')? leftElm.outerWidth() : 0), 0);
      const scrollbarWidth:number = (this.scrollService.verticalScrollbarShowing(scrollContainer)? this.scrollService.scrollbarWidth() : 0);
      return documentWidth - leftElementsWidth - scrollbarWidth;
    };

    const calculatePaddingTop:Function = ():number => {
      return this.element.outerHeight();
    };

    const setFixed:Function = ():void => {
      this.element.css({ position: 'fixed', width: calculateWidth(), 'z-index': 5 });
      if(this.innerClass){
        this.element.addClass(this.innerClass);
      }

      this.headerContainer.css('paddingTop', calculatePaddingTop());
      // Fix a layout bug on safari: when top frame changes, the header will otherwise lag behind
      // NGA-2068
      if (this.browserVendorService.safari) {
        setTimeout(() => this.element.hide().show(0));
      }
    };

    // update element only if the width / padding top changed
    if(!this.backupWidth || !this.backupPaddingTop || calculateWidth() !== this.backupWidth || calculatePaddingTop() !== this.backupPaddingTop) {
      setFixed();
    }
  };

  // do 100 checks in the first 5 seconds after 200 ms after the page loads
  private recalculateWidthAfterPageLoadListener():void {
    const PAGE_LOAD_DELAY:number = 200;
    const PERIOD:number = 200;
    const MAX_TIMES:number = 100;

    this.eventHandler = timer(PAGE_LOAD_DELAY, PERIOD)
      .subscribe((n:number) => {
        this.updateElementFixation();
        if(n === MAX_TIMES - 1){
          this.eventHandler.unsubscribe();
        }
      });
  }

  public ngOnDestroy():void {
    this.window.removeEventListener('resize', this.updateElementFixation);
    //remove element (as it was added somewhere else)
    this.element?.remove();
    this.headerContainer?.css('paddingTop', '');
    this.eventHandler?.unsubscribe();
  }
}